import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorCard } from "@/components/CalculatorCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Beaker, Zap, Ruler, ArrowRight, Brain, Calculator, FileText, Check, Download, Search, GraduationCap, Sparkles, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { Math } from "@/components/Math";
import { SuperCalcDemo } from "@/components/SuperCalcDemo";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { 
  organizationSchema, 
  websiteSchema, 
  softwareApplicationSchema, 
  faqSchema 
} from "@/utils/structuredData";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";

const featuredCalculators = [
  {
    title: "Molarity Calculator",
    description: "Calculate molar concentration of solutions",
    icon: Beaker,
    href: "/calculator/molarity",
    category: "chemistry" as const
  },
  {
    title: "Ohm's Law Calculator",
    description: "Find voltage, current, or resistance",
    icon: Zap,
    href: "/calculator/ohms-law",
    category: "physics" as const
  },
  {
    title: "Projectile Motion",
    description: "Calculate range, height, and flight time",
    icon: Ruler,
    href: "/calculator/projectile-motion",
    category: "physics" as const
  }
];

export default function Index() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { MEMBERSHIP_TIERS } = useSubscription();
  const [isCheckoutLoading, setIsCheckoutLoading] = useState<string | null>(null);
  
  // Set up language-aware structured data
  useLanguageAwareSEO([
    organizationSchema,
    websiteSchema,
    softwareApplicationSchema,
    faqSchema
  ]);

  const handleCheckout = async (priceId: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    
    const isStudentTier = priceId === MEMBERSHIP_TIERS.student.yearly.priceId || priceId === MEMBERSHIP_TIERS.student.monthly.priceId;
    if (isStudentTier && !user.email?.toLowerCase().endsWith(".edu")) {
      toast.error("Student & Educator membership requires a .edu email address");
      return;
    }
    
    try {
      setIsCheckoutLoading(priceId);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast.error(error?.message || "Failed to start checkout process");
    } finally {
      setIsCheckoutLoading(null);
    }
  };

  return (
    <PageLayout>
      <SEO 
        title="CalcLab - Free Science & Math Calculators | 100+ Tools Forever Free"
        description="Access 100+ completely free science calculators forever. Physics, chemistry, finance, and math tools with step-by-step solutions, LaTeX export, and SuperCalc AI. No subscriptions required."
        keywords="free calculator, physics calculator, chemistry calculator, math calculator, science calculator, online calculator, molarity calculator, ohms law calculator, compound interest calculator, quadratic equation solver, free math tools, LaTeX export, SuperCalc AI, step by step solutions"
      />
      
      {/* Hero Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Calculator className="h-4 w-4" />
            {t('home.hero.badge')}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
            {t('home.hero.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('home.hero.description')} <span className="text-foreground font-semibold">{t('home.hero.freeForever')}</span>. {t('home.hero.toolsDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button size="lg" asChild className="w-full sm:w-auto">
              <Link to="/supercalc" className="flex items-center justify-center">
                <span className="hidden sm:inline">{t('home.hero.trySupercalcFull')}</span>
                <span className="sm:hidden">{t('home.hero.trySupercalc')}</span>
                <Brain className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto">
              <Link to="/search">{t('home.hero.browseCalculators')}</Link>
            </Button>
          </div>
          
          {/* Install App CTA */}
          <div className="pt-4">
            <Button size="lg" variant="secondary" asChild className="gap-2 w-full sm:w-auto">
              <Link to="/install" className="flex items-center justify-center">
                <Download className="h-5 w-5" />
                <span className="hidden sm:inline">{t('home.hero.installAppFull')}</span>
                <span className="sm:hidden">{t('home.hero.installApp')}</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Free Features Section */}
      <section className="pb-8 md:pb-12">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl mb-2">{t('home.features.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.features.allCalculators')}</h3>
                    <p className="text-sm text-muted-foreground">{t('home.features.allCalculatorsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.features.stepByStep')}</h3>
                    <p className="text-sm text-muted-foreground">{t('home.features.stepByStepDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.features.latexExport')}</h3>
                    <p className="text-sm text-muted-foreground">{t('home.features.latexExportDesc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">{t('home.features.noAccount')}</h3>
                    <p className="text-sm text-muted-foreground">{t('home.features.noAccountDesc')}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* LaTeX Learning Center Promo */}
      <section className="pb-8 md:pb-12">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-primary/20 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <CardContent className="pt-6 relative">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-medium">
                    <GraduationCap className="h-4 w-4" />
                    {t('home.latexLearning.badge')}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold">
                    {t('home.latexLearning.title')}
                  </h2>
                  <p className="text-muted-foreground">
                    {t('home.latexLearning.description')}
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      {t('home.latexLearning.benefit1')}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      {t('home.latexLearning.benefit2')}
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary" />
                      {t('home.latexLearning.benefit3')}
                    </li>
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button asChild>
                      <Link to="/learn/latex" className="flex items-center">
                        {t('home.latexLearning.startLearning')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button variant="outline" asChild>
                      <Link to="/membership">{t('home.latexLearning.viewPlans')}</Link>
                    </Button>
                  </div>
                </div>
                <div className="hidden md:block">
                  <Card className="bg-slate-100 dark:bg-slate-200 border-0 shadow-lg">
                    <CardContent className="pt-6 space-y-4">
                      <div className="text-center space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('home.latexLearning.exampleOutput')}</p>
                        <div className="py-4">
                          <Math block>{"\\int_0^\\infty e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}"}</Math>
                        </div>
                        <code className="text-xs text-muted-foreground block bg-white/50 dark:bg-white/80 p-2 rounded">
                          \int_0^\infty e^{"{-x^2}"} dx = \frac{"{\\sqrt{\\pi}}"}{"{2}"}
                        </code>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <Sparkles className="h-3 w-3" />
                        {t('home.latexLearning.learnMore')}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SuperCalc AI Section */}
      <section className="py-8 md:py-12 bg-muted/30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Brain className="h-4 w-4" />
              {t('home.supercalc.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('home.supercalc.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('home.supercalc.description')}
            </p>
          </div>

          <Card className="bg-gradient-to-br from-primary/5 to-accent/5 overflow-hidden">
            <CardContent className="pt-6">
              <div className="grid lg:grid-cols-2 gap-6 overflow-hidden">
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold">{t('home.supercalc.howItWorks')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">1</div>
                      <p className="text-sm pt-0.5">{t('home.supercalc.step1')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">2</div>
                      <p className="text-sm pt-0.5">{t('home.supercalc.step2')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">3</div>
                      <p className="text-sm pt-0.5">{t('home.supercalc.step3')}</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold flex-shrink-0">4</div>
                      <p className="text-sm pt-0.5">{t('home.supercalc.step4')}</p>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('home.supercalc.outputStyles')}</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-background/50 p-2 rounded border">
                        <p className="font-semibold mb-1">{t('home.supercalc.quickAnswer')}</p>
                        <p className="text-muted-foreground">{t('home.supercalc.quickAnswerDesc')}</p>
                      </div>
                      <div className="bg-background/50 p-2 rounded border">
                        <p className="font-semibold mb-1">{t('home.supercalc.stepByStepStyle')}</p>
                        <p className="text-muted-foreground">{t('home.supercalc.stepByStepStyleDesc')}</p>
                      </div>
                      <div className="bg-background/50 p-2 rounded border">
                        <p className="font-semibold mb-1">{t('home.supercalc.detailedExplanation')}</p>
                        <p className="text-muted-foreground">{t('home.supercalc.detailedExplanationDesc')}</p>
                      </div>
                      <div className="bg-background/50 p-2 rounded border">
                        <p className="font-semibold mb-1">{t('home.supercalc.teachingGuide')}</p>
                        <p className="text-muted-foreground">{t('home.supercalc.teachingGuideDesc')}</p>
                      </div>
                    </div>
                    
                    <div className="mt-6 pt-4 border-t text-center">
                      <p className="text-lg md:text-xl font-bold text-foreground leading-relaxed">
                        {t('home.supercalc.builtBy')}
                      </p>
                      <p className="text-base md:text-lg font-semibold text-muted-foreground mt-2">
                        {t('home.supercalc.stemEducation')}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-muted-foreground mb-3">{t('home.supercalc.liveDemo')}</p>
                  <SuperCalcDemo />
                </div>
              </div>
              <div className="mt-6 text-center">
                <Button asChild size="lg" className="gap-2 w-full sm:w-auto">
                  <Link to="/supercalc" className="flex items-center justify-center">
                    <Brain className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('home.supercalc.tryButtonFull')}</span>
                    <span className="sm:hidden">{t('home.supercalc.tryButton')}</span>
                  </Link>
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('home.supercalc.additionalQueries')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('home.pricing.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('home.pricing.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Monthly Plan */}
            <Card className="relative transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t('home.pricing.monthly.title')}</CardTitle>
                <CardDescription>{t('home.pricing.monthly.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-4xl font-bold">$8.80</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.tokens')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.learningCenter')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.stepByStep')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.prioritySupport')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full" 
                  onClick={() => handleCheckout(MEMBERSHIP_TIERS.paid.monthly.priceId)}
                  disabled={isCheckoutLoading === MEMBERSHIP_TIERS.paid.monthly.priceId}
                >
                  {isCheckoutLoading === MEMBERSHIP_TIERS.paid.monthly.priceId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {t('home.pricing.getStarted')}
                </Button>
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className="relative border-primary shadow-lg scale-105 transition-all duration-300 hover:scale-110 hover:shadow-2xl">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                {t('home.pricing.yearly.badge')}
              </div>
              <CardHeader>
                <CardTitle className="text-2xl">{t('home.pricing.yearly.title')}</CardTitle>
                <CardDescription>{t('home.pricing.yearly.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-4xl font-bold">$88</span>
                  <span className="text-muted-foreground">/year</span>
                  <div className="text-sm text-green-600 font-medium">$7.33/month</div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.tokens')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.learningCenter')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.stepByStep')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.prioritySupport')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full" 
                  onClick={() => handleCheckout(MEMBERSHIP_TIERS.paid.yearly.priceId)}
                  disabled={isCheckoutLoading === MEMBERSHIP_TIERS.paid.yearly.priceId}
                >
                  {isCheckoutLoading === MEMBERSHIP_TIERS.paid.yearly.priceId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {t('home.pricing.getStarted')}
                </Button>
              </CardContent>
            </Card>

            {/* Student/Educator Plan */}
            <Card className="relative transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t('home.pricing.student.title')}</CardTitle>
                <CardDescription>{t('home.pricing.student.description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-4xl font-bold">$4.40</span>
                  <span className="text-muted-foreground">/month</span>
                  <div className="text-sm text-muted-foreground">{t('home.pricing.student.billedYearly')}</div>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.tokens')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.learningCenter')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.stepByStep')}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{t('home.pricing.features.futureCourses')}</span>
                  </li>
                </ul>
                <Button 
                  className="w-full" 
                  variant="outline"
                  onClick={() => handleCheckout(MEMBERSHIP_TIERS.student.yearly.priceId)}
                  disabled={isCheckoutLoading === MEMBERSHIP_TIERS.student.yearly.priceId}
                >
                  {isCheckoutLoading === MEMBERSHIP_TIERS.student.yearly.priceId ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {t('home.pricing.getStarted')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-8 md:py-12 bg-muted/30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Why Choose SuperCalc AI?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Built specifically for science and math, not general chatting
            </p>
          </div>

          {/* CalcLab Features Comparison */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">CalcLab Features</CardTitle>
              <CardDescription>Compare what's free forever vs premium AI assistance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Feature</th>
                      <th className="text-center py-3 px-4 font-semibold">Free Forever</th>
                      <th className="text-center py-3 px-4 font-semibold">SuperCalc AI</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="py-3 px-4">100+ Science & Math Calculators</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Step-by-Step Solutions</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">LaTeX Export</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">AI Problem Solver (any question)</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">1 free query</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Lesson Plan Generation</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Complex Problem Solving</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Priority Support</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* AI Comparison */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">SuperCalc AI vs General AI Chatbots</CardTitle>
              <CardDescription>Purpose-built for accuracy in science and mathematics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-semibold">Feature</th>
                      <th className="text-center py-3 px-4 font-semibold">SuperCalc AI</th>
                      <th className="text-center py-3 px-4 font-semibold">ChatGPT Plus</th>
                      <th className="text-center py-3 px-4 font-semibold">Claude Pro</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="py-3 px-4 font-medium">Monthly Price</td>
                      <td className="text-center py-3 px-4 text-primary font-bold">$8.80</td>
                      <td className="text-center py-3 px-4">$20</td>
                      <td className="text-center py-3 px-4">$20</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Science & Math Specialization</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4 text-muted-foreground">General purpose</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">General purpose</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Reduced Hallucination on STEM</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4 text-muted-foreground">Higher risk</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">Higher risk</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">100+ Built-in Calculators</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">LaTeX Output</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4">Limited</td>
                      <td className="text-center py-3 px-4">Limited</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Lesson Plan Templates</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Student/Educator Discount</td>
                      <td className="text-center py-3 px-4 text-primary font-semibold">$4.40/mo</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Verification of Solutions</td>
                      <td className="text-center py-3 px-4"><Check className="h-5 w-5 text-green-500 mx-auto" /></td>
                      <td className="text-center py-3 px-4 text-muted-foreground">Manual only</td>
                      <td className="text-center py-3 px-4 text-muted-foreground">Manual only</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm font-semibold mb-2 text-primary">Why SuperCalc AI is Different:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Purpose-Built:</strong> Trained and optimized specifically for science and mathematics, not general conversation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Lower Hallucination:</strong> Specialized training reduces incorrect answers on STEM problems by cross-referencing with verified calculators</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Educational Focus:</strong> Designed for students and educators with lesson plan generation, step-by-step explanations, and homework help</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span><strong>Better Value:</strong> 56% less expensive than general AI chatbots while delivering superior results for STEM subjects</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Calculators Overview Section */}
      <section className="py-8 md:py-12 bg-muted/30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            100+ Calculators at Your Fingertips
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            From basic math to advanced physics, chemistry, and finance. Everything you need for homework, projects, and exams.
          </p>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="text-center">
              <CardContent className="pt-6">
                <Calculator className="h-8 w-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">30+</div>
                <div className="text-sm text-muted-foreground">Math Calculators</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Zap className="h-8 w-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">40+</div>
                <div className="text-sm text-muted-foreground">Physics Calculators</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Beaker className="h-8 w-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">30+</div>
                <div className="text-sm text-muted-foreground">Chemistry Calculators</div>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-6">
                <Ruler className="h-8 w-8 mx-auto mb-3 text-primary" />
                <div className="text-2xl font-bold mb-1">20+</div>
                <div className="text-sm text-muted-foreground">Finance Calculators</div>
              </CardContent>
            </Card>
          </div>

          <Button size="lg" variant="outline" asChild>
            <Link to="/search">
              Browse All Calculators
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Popular Calculators Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 text-center">
            Popular Calculators
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {featuredCalculators.map((calc) => (
              <CalculatorCard key={calc.href} {...calc} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Button asChild variant="outline">
              <Link to="/search">
                View All Calculators
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* LaTeX Feature Section */}
      <section className="py-8 md:py-12 bg-muted/30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight mb-4">
                Professional LaTeX Output
              </h2>
              <p className="text-muted-foreground mb-6">
                Every calculator includes professionally formatted LaTeX output. Perfect for homework assignments, lab reports, and presentations.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>One-click copy to clipboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Works with all major LaTeX editors</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span>Beautifully rendered formulas</span>
                </li>
              </ul>
            </div>
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm">
                  <div className="text-muted-foreground mb-2">LaTeX Code:</div>
                  <code className="text-xs">
                    F = ma \\
                    F = (10 \text&#123; kg&#125;)(9.8 \text&#123; m/s&#125;^2) \\
                    F = 98 \text&#123; N&#125;
                  </code>
                </div>
                <div className="bg-background p-4 rounded-lg border">
                  <div className="text-muted-foreground mb-2 text-sm">Rendered Output:</div>
                  <Math block>F = ma \\ F = (10 \text&#123; kg&#125;)(9.8 \text&#123; m/s&#125;^2) \\ F = 98 \text&#123; N&#125;</Math>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Why Students & Educators Love CalcLab
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 mt-8">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-4xl mb-2">⚡</div>
                <h3 className="font-semibold mb-2">Lightning Fast</h3>
                <p className="text-sm text-muted-foreground">Get answers in seconds, not minutes</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-4xl mb-2">✓</div>
                <h3 className="font-semibold mb-2">Always Accurate</h3>
                <p className="text-sm text-muted-foreground">Thoroughly tested and verified</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="text-4xl mb-2">📚</div>
                <h3 className="font-semibold mb-2">Learn as You Go</h3>
                <p className="text-sm text-muted-foreground">See the steps, understand the math</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
